import Utilities from '@/Utility/Utilities';
import * as CoreFrameInterfaces from '#/CoreFrameInterfaces';
import ResourceManager from '@/Management/ResourceManager';
import ComponentManager from '@/Management/ComponentManager';
import ControlViewModel from '@/ViewModels/ControlViewModel';
import LabelViewModelT from '@/ViewModels/Controls/LabelViewModel';
import FilterViewModelT from '@/ViewModels/Controls/FilterViewModel';
import DropDownListViewModelT from '@/ViewModels/Controls/DropDownListViewModel';

import { SortElement } from '#/DefaultViewExtensionInterfaces';
import SortDirectionLinkViewModelT from '@/ViewModels/Controls/SortDirectionLinkViewModel';

/**
 * Das ViewModel für die Filter Sort Elemente
 */
export default class FilterSortElementViewModel extends ControlViewModel {
    private LabelViewModel = ComponentManager.getViewModel<typeof LabelViewModelT>('LabelViewModel');
    private DropDownListViewModel = ComponentManager.getViewModel<typeof DropDownListViewModelT>('DropDownListViewModel');
    private SortDirectionLinkViewModel = ComponentManager.getViewModel<typeof SortDirectionLinkViewModelT>('SortDirectionLink');

    public index: KnockoutObservable<number>;

    public indexLabel: LabelViewModelT;

    public sortColumn: KnockoutObservable<string>;
    public sortColumnList: DropDownListViewModelT;

    public filterViewModel: FilterViewModelT;

    public sortDirection: KnockoutObservable<any>;
    public sortDirectionLink: SortDirectionLinkViewModelT;

    public suspendForwarding = false;

    public constructor(params: ConstructorParameters) {
        super(params);

        this.filterViewModel = this.paramOrDefault(params.filterViewModel, null);

        this.index = this.defineComputed(() => {
            return this.filterViewModel.sortElements.indexOf(this);
        });

        this.defineSubscription(this.index,
            (value) => {
                this.locator(value + '');
            });

        this.indexLabel = new this.LabelViewModel({
            prefix: `${this.index() + 1}. `,
            ergName: 'SORT_ELEMENT_INDEX_LABEL',
            parentLocator: this.locator,
            locator: 'sortIndexLabel'
        });

        const isEnabled = this.defineComputed(() => {
            if (!this.isEnabled())
                return false;

            let sortElements = this.filterViewModel.sortElements();
            let index = sortElements.indexOf(this);

            if (index === 0)
                return true;

            let prevElement = sortElements[index - 1];
            if (!Utilities.isNullOrUndefined(prevElement))
                return prevElement.hasSortColumn();
            else
                return false;
        });

        this.sortColumn = this.observableOrDefault(params.sortColumn, null);
        this.sortColumnList = new this.DropDownListViewModel({
            value: this.sortColumn,
            data: this.defineComputed(() => {
                let alreadyPickedColumns = this.filterViewModel.sortElements().map(el => el.sortColumn());

                return this.filterViewModel.columns()
                    .filter(column => column.isSortable !== false && column.hidden !== true)
                    .filter(column => alreadyPickedColumns.indexOf(column.field) === -1 || column.field === this.sortColumn())
                    .map(column => {
                        return {
                            title: column.title,
                            field: column.field
                        }
                    });
            }),
            optionLabel: ' ',
            height: 500,
            isAutoWidth: false,
            dataTextField: 'title',
            dataValueField: 'field',
            isEnabled: isEnabled,
            parentLocator: this.locator,
            locator: 'sortColumnList'
        });

        this.sortDirection = this.observableOrDefault(params.sortDirection, 'ASC');
        this.sortDirectionLink = new this.SortDirectionLinkViewModel({
            parentLocator: this.locator,
            locator: 'button.change-sortDirection',
            isFocusable: true,
            isToolBarLink: true,
            direction: this.sortDirection
        });

        // adds or removes sort elements depending on the sort-column-selection
        this.defineSubscription(this.sortColumnList.value, (value) => {
            this.filterViewModel.rearrange();

            this.updateOrderByInFilterViewModel();
        });

        this.defineSubscription(this.sortDirectionLink.direction, (direction) => {
            this.sortDirection(direction);
            this.updateOrderByInFilterViewModel();
        });

        // sync index-label on index change
        this.defineSubscription(this.index, index => {
            this.indexLabel.prefix.caption(`${index + 1}. `);
            this.indexLabel.refreshCaption.valueHasMutated();
        });

        this.undisposeProperties.push('filterViewModel');
    }

    private updateOrderByInFilterViewModel() {
        if (this.isDisposed || this.suspendForwarding === true) {
            return;
        }

        this.filterViewModel.refreshSorting();
    }

    public updateElement(updateData: SortElement) {
        this.sortDirectionLink.direction(updateData.dir);
        this.sortColumnList.value(updateData.field);
    }

    public hasSortColumn(): boolean {
        return !!this.sortColumnList.value();
    }
}

interface ConstructorParameters extends CoreFrameInterfaces.IControlViewModelConstructorParameter {
    index?: number;
    filterViewModel: FilterViewModelT;
    sortColumn?: any;
    sortDirection?: 'ASC' | 'DESC';
}

ComponentManager.setViewModel('FilterSortElementViewModel', FilterSortElementViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');